import { useCallback, useEffect, useRef, useState } from 'react';
import type { TOCNode } from '../types/DocumentTypes';
import type { ProgressData } from './ProgressState';
import {
  ModalState,
  ProgressManager,
  ReadingSessionInitResult,
} from './ProgressManager';

export function useProgressTracking(
  isbn: string | undefined,
  getAccessToken: () => Promise<string | undefined>,
  totalBlocks: number | null | undefined,
) {
  const managerRef = useRef<ProgressManager | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);

  // Keep latest getAccessToken without re-initializing manager
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  const [progressData, setProgressData] = useState<ProgressData>({
    currentBlock: null,
    totalBlocks: null,
    progressPercentage: 0,
  });
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    data: null,
  });

  // Initialize ProgressManager and subscriptions
  useEffect(() => {
    if (!isbn || managerRef.current) {
      return undefined;
    }

    const manager = new ProgressManager(
      isbn,
      () => getAccessTokenRef.current(),
      totalBlocks ?? 0,
    );
    managerRef.current = manager;

    const unsubProgress = manager.subscribe((newState: ProgressData) => {
      setProgressData(newState);
    });

    const unsubModal = manager.subscribeToModal((state: ModalState) => {
      setModalState(state);
    });

    return () => {
      unsubProgress();
      unsubModal();
      managerRef.current?.destroy();
      managerRef.current = null;
    };
  }, [isbn, totalBlocks]);

  const trackProgress = useCallback(() => {
    const m = managerRef.current;
    if (!m) {
      return;
    }
    m.ensureTracking();
  }, []);

  const initReadingSession = useCallback(
    async (
      chapters: TOCNode[],
      params: URLSearchParams,
    ): Promise<ReadingSessionInitResult> => {
      const m = managerRef.current;
      if (!m) {
        throw new Error('ProgressManager not initialized');
      }
      return m.initializeReadingSession(chapters, params);
    },
    [],
  );

  const handleModalClose = useCallback(async (): Promise<void> => {
    const m = managerRef.current;
    if (!m) {
      return;
    }
    await m.dismissModal();
    // Ensure tracking resumes or refreshes after modal is closed
    m.ensureTracking();
  }, []);

  const handleContinueReading = useCallback(
    async (chapters: TOCNode[]): Promise<{ chapter: TOCNode }> => {
      const m = managerRef.current;
      if (!m) {
        throw new Error('ProgressManager not initialized');
      }
      const result = await m.handleContinueReading(chapters);
      return { chapter: result.chapter };
    },
    [],
  );

  return {
    progressData,
    modalState,
    trackProgress,
    initReadingSession,
    handleModalClose,
    handleContinueReading,
  };
}
